import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import TabPanel from '../../TabPanel';
import Itinerary from '../Itinerary';
import Rates from '../Rates';
import DeckPlans from '../DeckPlans';
import ShipDetails from '../ShipDetails';
import CruiseLine from '../CruiseLine';
import { itineraryRequest } from '../../../store/slices/cruise';
import { SearchStatus } from '../../../constants';

// TODO: move to constants
const CruiseDetailsTabIndex = {
  CABINS_RATES: 0,
  ITINERARY: 1,
  DECK_PLANS: 2,
  SHIP_DETAILS: 3,
  CRUISE_LINE: 4,
};
const CruiseDetails = ({
  onModalClose, tripId, itineraryId, cruiseItinerary, selectedItineraryRow, doShowPrice,
  travelOption, tripRoomSearchInput,
}) => {
  const selectedTabClasses = 'text-theme border-b-2 border-theme';
  const dispatch = useDispatch();
  const [selectedCruiseDetailsTab,
    setSelectedCruiseDetailsTab] = useState(CruiseDetailsTabIndex.CABINS_RATES);
  const {
    cruiseDetails,
    itineraryDetailError, itineraryDetailStatus,
  } = useSelector((state) => state.cruise);
  const { isViewingCompare } = useSelector((store) => store.compare);
  const hasItineraryDetail = itineraryDetailStatus === SearchStatus.DONE && !itineraryDetailError;
  const { track } = useAnalytics();

  const handleTabChange = (index) => {
    setSelectedCruiseDetailsTab(index);
    track('cruiseDetails.handleTabChange', { index });
  };

  useEffect(() => {
    if (!cruiseItinerary && itineraryId) dispatch(itineraryRequest(itineraryId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId]);

  return (
    <>
      <div className="bg-white overflow-x-auto border-b-2">
        {/* TODO: fix cruise sections menu for compare, the menu should scrollable
              horizontally on mobile to see the whole thing.
              Problem is related parent table element */}
        <div className={clsx('px-2 sm:px-8 grid-flow-col auto-cols-max gap-5 font-bold cursor-pointer', { grid: !isViewingCompare, 'sm:grid': isViewingCompare })}>
          <a
            className={clsx(selectedCruiseDetailsTab === CruiseDetailsTabIndex.CABINS_RATES
              && selectedTabClasses, 'py-4 w:auto sm:w-36 text-center', 'block sm:inline w-full sm:w-full')}
            tabIndex="0"
            role="button"
            onClick={() => { handleTabChange(CruiseDetailsTabIndex.CABINS_RATES); }}
            onKeyDown={() => { handleTabChange(CruiseDetailsTabIndex.CABINS_RATES); }}
          >
            Cabins & Rates
          </a>
          <a
            className={clsx(selectedCruiseDetailsTab === CruiseDetailsTabIndex.ITINERARY
              && selectedTabClasses, 'py-4 w:auto sm:w-24 text-center', 'block sm:inline')}
            role="button"
            tabIndex="-3"
            onClick={() => { handleTabChange(CruiseDetailsTabIndex.ITINERARY); }}
            onKeyDown={() => { handleTabChange(CruiseDetailsTabIndex.ITINERARY); }}
          >
            Itinerary
          </a>
          {hasItineraryDetail && (
            <>
              <a
                className={clsx(selectedCruiseDetailsTab === CruiseDetailsTabIndex.DECK_PLANS
                  && selectedTabClasses, 'py-4 w:auto sm:w-24 text-center', 'block sm:inline')}
                tabIndex="-2"
                role="button"
                onClick={() => { handleTabChange(CruiseDetailsTabIndex.DECK_PLANS); }}
                onKeyDown={() => { handleTabChange(CruiseDetailsTabIndex.DECK_PLANS); }}
              >
                Deck plans
              </a>
              <a
                className={clsx(selectedCruiseDetailsTab === CruiseDetailsTabIndex.SHIP_DETAILS
                  && selectedTabClasses, 'py-4 w:auto sm:w-24 text-center', 'block sm:inline')}
                tabIndex="-1"
                role="button"
                onClick={() => { handleTabChange(CruiseDetailsTabIndex.SHIP_DETAILS); }}
                onKeyDown={() => { handleTabChange(CruiseDetailsTabIndex.SHIP_DETAILS); }}
              >
                Ship details
              </a>
              <a
                className={clsx(selectedCruiseDetailsTab === CruiseDetailsTabIndex.CRUISE_LINE
                  && selectedTabClasses, 'py-4 w:auto sm:w-24 text-center', 'block sm:inline')}
                tabIndex="0"
                role="button"
                onClick={() => { handleTabChange(CruiseDetailsTabIndex.CRUISE_LINE); }}
                onKeyDown={() => { handleTabChange(CruiseDetailsTabIndex.CRUISE_LINE); }}
              >
                Cruise line
              </a>
            </>
          )}
        </div>
      </div>
      <TabPanel value={selectedCruiseDetailsTab} index={CruiseDetailsTabIndex.ITINERARY}>
        <Itinerary
          itineraryDays={cruiseDetails && cruiseDetails.itineraryDays}
          sailingItineraryMapImageUrl={cruiseDetails && cruiseDetails.sailingItineraryMapImageUrl}
        />
      </TabPanel>
      <TabPanel value={selectedCruiseDetailsTab} index={CruiseDetailsTabIndex.DECK_PLANS}>
        <DeckPlans deckPlans={cruiseDetails && cruiseDetails.ship
          && cruiseDetails.ship.shipDecks}
        />
      </TabPanel>
      <TabPanel value={selectedCruiseDetailsTab} index={CruiseDetailsTabIndex.SHIP_DETAILS}>
        <ShipDetails ship={cruiseDetails && cruiseDetails.ship} />
      </TabPanel>
      <TabPanel value={selectedCruiseDetailsTab} index={CruiseDetailsTabIndex.CRUISE_LINE}>
        <CruiseLine cruiseLine={cruiseDetails && cruiseDetails.cruiseLine} />
      </TabPanel>
      <Rates className="border-t-2" onModalClose={onModalClose} tripId={tripId} itineraryId={itineraryId} cruiseItinerary={cruiseItinerary} selectedItineraryRow={selectedItineraryRow} doShowPrice={doShowPrice} searchResultsTravelOption={travelOption} tripRoomSearchInput={tripRoomSearchInput} />
    </>
  );
};

CruiseDetails.propTypes = {
  onModalClose: PropTypes.func,
  tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  itineraryId: PropTypes.string,
  cruiseItinerary: PropTypes.shape({}),
  selectedItineraryRow: PropTypes.objectOf(PropTypes.objectOf),
  doShowPrice: PropTypes.bool,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
  }),
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

CruiseDetails.defaultProps = {
  onModalClose: undefined,
  tripId: undefined,
  itineraryId: undefined,
  cruiseItinerary: undefined,
  selectedItineraryRow: undefined,
  doShowPrice: true,
  travelOption: undefined,
  tripRoomSearchInput: undefined,
};

export default CruiseDetails;
